import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { Label, Tab, TabProps, Input, InputProps, Grid, Button as SuiButton, Dropdown, DropdownProps, Modal } from 'semantic-ui-react';
import { SemanticDatepickerProps } from 'react-semantic-ui-datepickers/dist/types';
import dayjs, { Dayjs } from 'dayjs';
import ColoredTab from '../components/elements/ColoredTab';
import Button from '../components/elements/Button';
import EditDebtor, { InitialDebtorInfo, DebtorEditInfo } from '../components/controls/EditDebtor';
import { Account, Claim, PhoneNumber } from '../models/GeneralModels';
import DateInput from '../components/elements/DateInput';
import Uploader from '../components/controls/Uploader';
import API from '../utils/network';
import RawAPI, { AxiosRequestConfig } from 'axios';
import { stringIsNullOrUndefinedOrBlank, stringIsNumber } from '../utils/siteFunctions';
import UploadProgress, { FileWithID } from '../components/controls/UploadProgress';
import { reduce } from 'lodash';
import { addPopupMessage } from '../store/reducers/ui/actions';
import mime from 'mime/lite';

const EnterClaimsPage = ():React.ReactElement => {
	/// Hooks
	const history = useHistory();
	const dispatch = useDispatch();
	
	/// Global State
	const primaryColor = useSelector(state => state.ui.customer.primaryColor);
	const client = useSelector(state => state.ui.client);

	/// Local State
	const [ debtors, setDebtors ] = useState<DebtorEditInfo[]>([ { ...InitialDebtorInfo }, { ...InitialDebtorInfo } ]);
	const [ currentTabIndex, setCurrentTabIndex ] = useState(0);
	const [ account, setAccount ] = useState<Account>(InitialAccountInfo);
	const [ filesToUpload, setFilesToUpload ] = useState<File[]>([]);
	const [ uploadingFiles, setUploadingFiles ] = useState<FileWithID[]>([]);
	const [ uploadNote, setUploadNote ] = useState('');
	const [ cancelModalOpen, setCancelModalOpen ] = useState(false);
	const [ submitting, setSubmitting ] = useState(false);
	const [ callbackAfterSubmit, setCallbackAfterSubmit ] = useState<(() => any) | undefined>(undefined);
	const [ debtorMinFieldsError, setDebtorMinFieldsError ] = useState(false);
	const [ requiredDocuments, setRequiredDocuments ] = useState<string[]>([]);

	/// Local Variables
	const tabs = [
		{ menuItem: 'Primary Client' },
		{ menuItem: 'Spouse/3rd Party Payor' }
	];

	/// Events
	const handleDebtorChange = value => {
		if (!submitting) {
			setDebtorMinFieldsError(false);
			setDebtors(prevState => prevState.slice(0, currentTabIndex).concat([ value ], prevState.slice(currentTabIndex + 1)));
		}
	};

	const handleTabChange = (e, { activeIndex }: TabProps) => {
		setCurrentTabIndex(activeIndex as number);
	};

	const handleAccountChange = (e, { value, name }: InputProps | DropdownProps | SemanticDatepickerProps) => {
		if (!submitting) {
			setAccount(prevState => ({
				...prevState,
				[name]: value
			}));
		}
	};

	const handleAccountClaimChange = (e, { value, name }: InputProps | DropdownProps | SemanticDatepickerProps) => {
		if (!submitting) {
			setAccount(prevState => ({
				...prevState,
				claims: [ {
					...prevState.claims?.[0] as Claim,
					[name]: value
				}]
			}));
		}
	};

	const handleAccountClaimNumberChange = (e, { value, name }: InputProps | DropdownProps) => {
		if (!submitting) {
			if (stringIsNumber(value) || value === '') {
				setAccount(prevState => ({
					...prevState,
					claims: [ {
						...prevState.claims?.[0] as Claim,
						[name]: value === '' ? undefined : Number(value)
					}]
				}));
			}
		}
	};

	const handleUploaderChange = (files: File[]) => {
		if (!submitting)
			setFilesToUpload(files);
	};

	const handleSubmitAndCloseClick = e => {
		if (!submitting)
			triggerSubmitAccount(redirectToHome);
	};

	const handleSubmitAndNewClick = e => {
		if (!submitting)
			triggerSubmitAccount(clearPage);
	};

	const handleUploadNoteChange = (e, { value }) => {
		setUploadNote(value);
	};

	/// Functions
	const clearPage = () => {
		setDebtors([ { ...InitialDebtorInfo }, { ...InitialDebtorInfo }]);
		setCurrentTabIndex(0);
		setAccount(InitialAccountInfo);
		setFilesToUpload([]);
		setUploadingFiles(prevState => []);
		setCancelModalOpen(false);
	};

	const redirectToHome = () => {
		history.push('/');
	};

	const triggerSubmitAccount = (callback: () => any) => {
		setCallbackAfterSubmit(() => callback);

		const missingDocs = requiredDocuments.filter(x => !filesToUpload.some(y => y.name === x));

		if (missingDocs.length > 0) {
			dispatch(addPopupMessage(
				(<div style={{textAlign: 'center'}}>The following required document{missingDocs.length > 1 ? 's are' : ' is'} missing from the upload:<b>{missingDocs.map((x, index) => <div key={index}>{x}</div>)}</b><br />You can Go Back and upload them now or Continue and upload them later</div>),
				'Action Required',
				'warning sign',
				[
					{
						text: 'Continue',
						onClick: () => submitAccount(callback)
					},
					{
						text: 'Go Back',
						secondary: true
					}
				]));
		}
		else {
			submitAccount(callback);
		}
	};

	const submitAccount = (callback: () => any) => {
		setSubmitting(true);

		// build debtor list
		const debtorsDTO: any[] = [];

		let isPrimary = true;
		for (let i = 0; i < debtors.length; i++) {
			const d = debtors[i];
			const da = d?.addresses?.[0];
			const de = d?.emailAddresses?.[0];
			const dp = d?.phoneNumbers;

			const phoneNumbers: PhoneNumber[] = [];

			for (let j = 0; j < (dp?.length || 0); j++) {
				if (!stringIsNullOrUndefinedOrBlank(dp?.[j].phoneNumber))
					phoneNumbers.push(dp?.[j] as PhoneNumber);
			}

			if ((d.isCompany && d.companyName) || (!d.isCompany && (d.firstName || d.lastName))) {
				debtorsDTO.push({
					// Id: 0,
					FirstName: d.isCompany ? null : d.firstName,
					MiddleName: d.isCompany ? null : d.middleName,
					LastName: d.isCompany ? null : d.lastName,
					AKA: d.isCompany ? null : d.aka,
					Prefix: null,
					Suffix: null,
					SSN: d.ssn,
					DateOfBirth: d.dob,
					CompanyName: d.isCompany ? d.companyName : null,
					IsCompany: d.isCompany,
					IsPrimary: isPrimary,
					EmployerName: d.employerName,
					// LastUpdated: null,
					DebtorAddresses: stringIsNullOrUndefinedOrBlank(d.addresses?.[0].address) ? [] : [ {
						Address: da?.address,
						AddressTwo: stringIsNullOrUndefinedOrBlank(da?.addressTwo) ? null : da?.addressTwo,
						City: stringIsNullOrUndefinedOrBlank(da?.city) ? null : da?.city,
						State: stringIsNullOrUndefinedOrBlank(da?.state) ? null : da?.state,
						Zip: stringIsNullOrUndefinedOrBlank(da?.zip) ? null : da?.zip,
						IsPrimary: true,
						Status: 'Good',
						Type: 'Primary'
					} ],
					DebtorEmailAddresses: stringIsNullOrUndefinedOrBlank(de?.emailAddress) ? [] : [ {
						Email: de?.emailAddress,
						IsPrimary: true,
						Status: 'Good'
					} ],
					DebtorPhoneNumbers: phoneNumbers.map(x => ({
						// Phone: null,
						UncleanedPhone: x.phoneNumber,
						IsPrimary: true,
						// Description: null,
						Status: 'Good',
						Type: x.phoneType
					}))
				});

				isPrimary = false; // Only true for the first debtor inserted
			}
		}

		if (debtorsDTO.length === 0) {
			triggerSubmitComplete(false);
			setDebtorMinFieldsError(true);
		}
		else {
			// build account
			const accountDTO = {
				ClientClaimNumber: account.clientClaimNumber,
				OriginatedDate: account.originatedDate,
				DateOfFirstDelinquency: account.dateOfFirstDelinquency,
				Debtors: debtorsDTO,
				FinancialClaims: account.claims?.[0].amount !== undefined ? account.claims?.[0].amount > 0 &&
					[
						{
							amount: account.claims?.[0].amount,
							InterestSteps: [ { rate: account.claims?.[0].interestRate || 0 } ],
							StartDate: account.claims?.[0].startDate || new Date(),
							note: account.claims?.[0].note
						}
					] : undefined
			};

			API.post('/account/add', accountDTO)
				.then(data => {
					if (stringIsNullOrUndefinedOrBlank(data.data.accountNumber)) {
						dispatch(addPopupMessage(data.data.message, 'Submitting Account', 'exclamation circle'));
						triggerSubmitComplete(false);
					}
					else if (filesToUpload.length > 0)
						uploadFiles(data.data.accountNumber);
					else
						triggerSubmitComplete(true, callback);
				})
				.catch(error => console.error('Error', error));
		}
	};

	const uploadFiles = (accountNumber: string) => {
		const preMap = filesToUpload.map((x, index) => ({
			id: uuidv4(),
			file: x
		}));

		setUploadingFiles(prevState => [ ...prevState, ...preMap.map(x => ({ id: x.id, file: x.file, progress: 0, completed: false})) ]);

		preMap.forEach((f, i) => {
			const fileExtension = f.file.name.includes('.') ? f.file.name.slice(f.file.name.lastIndexOf('.') + 1) : '';
			const mimeType = mime.getType(fileExtension);

			API.post('/account/account-file/add', {
				AccountNumber: accountNumber,
				FileLocation: 'S3',
				FileSize: f.file.size,
				FileName: f.file.name,
				FileExtension: `.${fileExtension}`,
				ModifiedDate: new Date(f.file.lastModified),
				ShowInClientPortal: true,
				FileType: mimeType,
				Comment: stringIsNullOrUndefinedOrBlank(uploadNote) ? null : uploadNote,
				// EnteredById: null,
				// DoclibFolderId: null
			})
				.then(data => {
					const newRecordId: number | null = data.data.fileId;

					API.post('/account/account-file/s3-url', {
						FileId: newRecordId,
						FileExtension: fileExtension,
						ContentType: mimeType
					})
						.then(data => {
							const config: AxiosRequestConfig = {
								headers: { 'content-type': mimeType },
								onUploadProgress: e => setUploadingFiles(prevState => prevState.map(x => x.id === f.id ? { ...x, progress: e.loaded, completed: e.loaded === x.file.size } : x))
							};

							RawAPI.put(data.data.signedUrl, f.file, config)
								.then(data => { /* Nothing to do here. A useEffect deals with the files once they're uploaded. */ })
								.catch(error => {
									console.error('Error', error);
									setUploadingFiles(prevState => prevState.map(x => x.id === f.id ? { ...x, error: error } : x));
								});
						})
						.catch(error => console.error('Error', error));
				})
				.catch(error => console.error('Error', error));
		});
	};

	const triggerSubmitComplete = useCallback((triggerCallback: boolean, callback: (() => any) | undefined = undefined) => {
		setSubmitting(false);
		if (triggerCallback) {
			if (callback)
				callback();
			else if (callbackAfterSubmit)
				callbackAfterSubmit();
		}
		setCallbackAfterSubmit(undefined);
	}, [callbackAfterSubmit]);

	/// Effects
	useEffect(() => {
		if (submitting && uploadingFiles.length > 0) {
			if (uploadingFiles.find(x => !x.completed && x.error === undefined) === undefined)
				triggerSubmitComplete(true);
		}
	}, [uploadingFiles, submitting, triggerSubmitComplete]);

	useEffect(() => {
		// Get list of required documents from the API
		API.get('/client/get-required-docs')
			.then(data => setRequiredDocuments(data.data.requiredFiles))
			.catch(error => console.error(error));
	}, []);

	/// Render
	return (
		<Grid
			stackable
			columns={2}
			style={{
				paddingTop: '1em'
			}}>
			<Grid.Row
				style={{
					borderBottom: `solid 3px ${primaryColor}`,
					paddingBottom: '0'
				}}
			>
				<Grid.Column>
					<ColoredTab panes={tabs} activeIndex={currentTabIndex} onTabChange={handleTabChange} />
				</Grid.Column>
				<Grid.Column
					style={{
						display: 'grid',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gridAutoFlow: 'column'
					}}
				>
					<SuiButton.Group>
						<Button secondary icon="save" content="Submit & New" compact disabled={submitting} loading={submitting} onClick={handleSubmitAndNewClick} />
						<Dropdown
							trigger={
								<SuiButton.Group>
									<span style={{ display: 'none' }} />{/* To trick the button into having a flat left side */}
									<Button secondary icon="dropdown" />
								</SuiButton.Group>
							}
							disabled={submitting}
						>
							<Dropdown.Menu>
								{/* <Dropdown.Item icon="save" text="Submit & Close" onClick={handleSubmitClick} /> */}
								<Dropdown.Item icon="save" text="Submit & Close" onClick={handleSubmitAndCloseClick} />
							</Dropdown.Menu>
						</Dropdown>
					</SuiButton.Group>
					<Modal
						open={cancelModalOpen}
						onOpen={e => setCancelModalOpen(true)}
						onClose={e => setCancelModalOpen(false)}
						trigger={
							<Button
								basic
								borderless
								content="Cancel"
								icon="cancel"
								// onClick={handleCancelClick}
							/>
						}
					>
						<Modal.Header>
							Are you sure you want to cancel?
						</Modal.Header>
						<Modal.Content>
							<Modal.Description>
								You have made changes to this page that will be
								lost if you cancel now. You can submit the account,
								discard your changes, or cancel to continue editing.
							</Modal.Description>
						</Modal.Content>
						<Modal.Actions>
							<Button onClick={e => setCancelModalOpen(false)}>Cancel</Button>
							<Button as={Link} to="/">Discard Changes</Button>
							<Button secondary onClick={handleSubmitAndCloseClick}>Submit</Button>
						</Modal.Actions>
					</Modal>
				</Grid.Column>
			</Grid.Row>
			<Grid.Row divided>
				<Grid.Column
					style={{
						textAlign: 'center',
						padding: '0 1rem 0 2rem'
					}}
				>
					<EditDebtor
						value={debtors[currentTabIndex]}
						onChange={handleDebtorChange}
						headerText={currentTabIndex === 0 ? 'Client Information' : 'Spouse/3rd Party Payor Information'}
						showIsCompanyOption={false}
						minFieldsError={currentTabIndex === 0 ? debtorMinFieldsError : undefined}
					/>
				</Grid.Column>
				<Grid.Column
					style={{
						textAlign: 'center'
					}}
				>
					<h2>Account Information</h2>
					<div style={{ maxWidth: '45em', width: '100%', display: 'inline-block', textAlign: 'left' }}>
						<div className="justified-form-row">
							<div className="justified-form-field" style={{ flexGrow: 2, flexShrink: 2 }}>
								Case number:
								<Input name="clientClaimNumber" value={account.clientClaimNumber} onChange={handleAccountChange} />
							</div>
							<div className="justified-form-field" style={{ minWidth: '1em' }}>
								Amount:
								<Input labelPosition='left' fluid type='text' placeholder='Amount' name="amount" value={account.claims?.[0].amount} onChange={handleAccountClaimNumberChange}>
									<Label basic>$</Label>
									<input />
								</Input>
							</div>
							{client.showFieldsSettings.AccountType &&
								<div className="justified-form-field" style={{ flexGrow: 1, flexShrink: 1 }}>
									Account Type:
									<Dropdown
										name="accountType"
										value={account.accountType}
										selection
										onChange={handleAccountChange}
										fluid
										options={[{
											key: 1,
											value: 1,
											text: 'NONCOMM'	
										}, {
											key: 2,
											value: 2,
											text: 'COMM'
										}]}
									/>
								</div>
							}
						</div>
						{(client.showFieldsSettings.InterestStartDate || client.permissions.EnterOwnInterestRates || client.showFieldsSettings.ClaimNote) &&
							<div className="justified-form-row">
								{client.showFieldsSettings.InterestStartDate &&
									<div className="justified-form-field">
										Start Date:
										<DateInput
											name="startDate"
											placeholder="Start Date..."
											value={account.claims?.[0].startDate}
											onChange={handleAccountClaimChange}
										/>
									</div>
								}
								{client.permissions.EnterOwnInterestRates &&
									<div className="justified-form-field">
										Interest Rate:
										<Input labelPosition='right' fluid type='text' placeholder='Interest Rate' name="interestRate" value={account.claims?.[0].interestRate} onChange={handleAccountClaimNumberChange}>
											<input />
											<Label basic>%</Label>
										</Input>
									</div>
								}
								{client.showFieldsSettings.ClaimNote &&
									<div className="justified-form-field">
										Claim Note:
										<Input name="note" value={account.claims?.[0].note} onChange={handleAccountClaimChange} />
									</div>
								}
							</div>
						}
						{(client.showFieldsSettings.OriginatedDate || client.showFieldsSettings.DateOfFirstDelinquency) && (
							<div className="justified-form-row">
								{client.showFieldsSettings.OriginatedDate && (
									<div className="justified-form-field">
										Originated Date
										<DateInput
											name="originatedDate"
											placeholder="Originated Date"
											value={account.originatedDate}
											onChange={handleAccountChange}
										/>
									</div>
								)}
								{client.showFieldsSettings.DateOfFirstDelinquency && (
									<div className="justified-form-field">
										Date of First Delinquency
										<DateInput
											name="dateOfFirstDelinquency"
											placeholder="Date of First Delinquency"
											value={account.dateOfFirstDelinquency}
											onChange={handleAccountChange}
										/>
									</div>
								)}
							</div>
						)}
					</div>
					<h2>Upload Files</h2>
					<Uploader value={filesToUpload} onChange={handleUploaderChange} style={{margin: '0 2em 1em 2em'}} />
					{client.showFieldsSettings.UploadNote &&
						<div>
							<Input value={uploadNote} onChange={handleUploadNoteChange} placeholder='File Note' fluid style={{margin: '0 2em'}} />
						</div>
					}
					{uploadingFiles.length > 0 && (
						<>
							<UploadProgress uploadingFiles={uploadingFiles} />
						</>
					)}
				</Grid.Column>
			</Grid.Row>
		</Grid>
	);
};

const InitialAccountInfo: Account = {
	clientClaimNumber: '',
	accountType: 1,
	claims: [ {
		amount: undefined,
		startDate: new Date(),
		interestRate: undefined,
		note: undefined
	} ]
};

export default EnterClaimsPage;